<template>
  <div
    v-if="show"
    class="today-widget-component-container"
  >
    <div class="heading">
      <dashboard-widget-header
        :icon="widgetIcon"
        :title="widgetTitle"
      />
    </div>
    <div class="widget-container">
      <rate-limit-widget
        v-if="shouldShowRateLimitUi"
        :config="config"
        :dashboard="dashboard"
      />
      <button
        v-else
        class="left"
        @click="todayAction"
      >
        <div :class="['status', { completed: dailyGoalMet }]">
          <icon
            v-if="dailyGoalMet"
            name="check"
            class="completed-icon"
            variant="white"
          />
        </div>
        <div class="counter-container">
          <circle-progress
            v-if="studyGoal"
            :cap="'butt'"
            :numerator="progressNumerator"
            :denominator="studyGoal"
            :thickness="8"
          >
            <div class="today-count">
              {{ dailyCountDisplay }}
            </div>
          </circle-progress>
          <div
            v-else
            class="empty-state"
          >
            <icon
              name="check"
              variant="white"
              class="icon"
            />
          </div>
        </div>
        <div
          class="today-text-button"
          @click="todayAction"
        >
          <div class="heading">
            {{ heading }}
          </div>
          <div class="subheading">
            {{ subheading }}
          </div>
        </div>
      </button>
      <div class="right">
        <div class="calendar-heading">
          <div class="month">
            {{ monthDisplay }}
          </div>
          <button @click="toggleWeek(-1)">
            <icon :name="'chevron-left'" />
          </button>
          <button
            :class="[{ show: currentWeek !== 0 }, 'reset']"
            @click="resetWeek()"
          >
            <icon :name="'refresh'" />
          </button>
          <button @click="toggleWeek(1)">
            <icon
              :name="'chevron-left'"
              class="right-arrow"
            />
          </button>
        </div>
        <div class="calendar">
          <button
            v-for="date in dates"
            :key="date.key"
            :class="{ disabled: date.future }"
            @click="selectCalendarDate(date)"
          >
            <div class="letter">
              {{ date.letter }}
            </div>
            <div :class="[{ completed: date.answeredAtLeastOne, current: date.current, future: date.future }, 'day']">
              {{ date.day }}
            </div>
            <div :class="[{ active: date.active }, 'indicator']" />
          </button>
        </div>
        <div class="streak-notch">
          {{ streakText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { addDays, addWeeks, differenceInDays, eachDayOfInterval, format, isAfter, isSameDay, startOfWeek } from 'date-fns';

import CircleProgress from '@components/singletons/circle-progress';
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
// import HltText from '@/components/ui/text.vue';
import Icon from '@components/singletons/icon';
import RateLimitWidget from '@components/dashboard-widgets/rateLimitWidget.vue';
import RateLimitMixin from '@/mixins/rate-limit';

import { flashcardStatsByDay } from '@/utils/flashcards';

export default {
  components: { CircleProgress, DashboardWidgetHeader, Icon, RateLimitWidget},
  mixins: [RateLimitMixin],
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      currentWeek: 0,
      selectedDay: new Date(), // the day selected on the widget
      show: true,
    };
  },
  computed: {
    dailyCount() {
      if (this.onCurrentDay) {
        return this.$store.getters['flashcardStates/questionsAnsweredToday'];
      } else {
        return flashcardStatsByDay(this.flashcardStats, this.selectedDay);
      }
    },
    dailyCountDisplay() {
      return this.dailyCount < 10 ? `0${this.dailyCount}` : this.dailyCount;
    },
    dailyGoalMet() {
      return this.studyGoal && this.studyGoal <= this.dailyCount;
    },
    dates() {
      let now = addWeeks(new Date(), this.currentWeek);
      const startDate = startOfWeek(now, { weekStartsOn: 1 });
      const endDate = addDays(startDate, 6);
      const range = eachDayOfInterval({ start: startDate, end: endDate });
      return range.map(date => {
        const sameDay = isSameDay(date, new Date());
        const answered = sameDay ? this.$store.getters['flashcardStates/questionsAnsweredToday'] : flashcardStatsByDay(this.flashcardStats, date);

        return {
          active: isSameDay(date, this.selectedDay),
          current: sameDay,
          future: isAfter(date, new Date()),
          key: date.toString(),
          letter: format(date, 'EEEEE'),
          day: format(date, 'd'),
          goalMet: answered >= this.studyGoal && this.studyGoal > 0,
          answeredAtLeastOne: answered > 0,
          date,
        };
      });
    },
    flashcardStats() {
      return this.$store.getters['flashcardStates/flashcardStats'];
    },
    goals() {
      return this.config.config.todaySuggestedGoals;
    },
    heading() {
      return this.config.config.todayGoalCardTitle;
    },
    currentStreak() {
      let currentStreak = 0;
      if (this.flashcardStats?.flashcard) {
        const stats = this.flashcardStats.flashcard.map(stat => stat);
        const sortedStats = stats.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (!sortedStats?.length) {
          return currentStreak;
        }

        const today = new Date();
        if (Math.abs(differenceInDays(today, new Date(sortedStats[0]?.date))) > 1) {
          return currentStreak;
        } else {
          for(let i = 0; i < sortedStats.length; i++) {
            const currentStatDate = new Date(sortedStats[i]?.date);
            const nextStatDate = new Date(sortedStats[i + 1]?.date);
            if (Math.abs(differenceInDays(currentStatDate, nextStatDate)) > 1) {
              break;
            } else {
              currentStreak++;
            }
          }
        }
      }
      return currentStreak;
    },
    monthDisplay() {
      // This should always be 7 but the minute I hardcode it, the minute it'll get weird
      const datesLength = this.dates.length;
      const firstMonth = format(this.dates[0].date, 'MMMM');
      const secondMonth = format(this.dates[datesLength - 1].date, 'MMMM');
      if (firstMonth !== secondMonth) {
        return `${firstMonth} - ${secondMonth}`;
      } else {
        return format(this.dates[0].date, 'MMMM');
      }
    },
    navigationItem() {
      return this.$store.getters['navigationItems/navigationItems'][this.config.config.todayQuickStartNavItemId];
    },
    onCurrentDay() {
      return isSameDay(new Date(), this.selectedDay);
    },
    progressNumerator() {
      return this.dailyGoalMet ? this.studyGoal : this.dailyCount;
    },
    streakText() {
      if (this.currentStreak > 0) {
        return `🔥 ${this.currentStreak} Day Streak`;
      } else {
        return '🔥 Start A New Streak';
      }
    },
    studyGoal() {
      return this.$store.getters['session/currentUser'].userProfile?.studyGoal;
    },
    subheading() {
      return this.studyGoal ? `Answer ${this.studyGoal} Questions` : 'Set Your Daily Goal';
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
    widgetIcon() {
      if (this.config) {
        return this.$store.getters['attachments/get'][this.config.iconAttachmentId];
      }
    },
    widgetTitle() {
      if (this.onCurrentDay) {
        return this.config.name;
      }
      return format(this.selectedDay, 'MMMM d');
    },
    shouldShowRateLimitUi() {
      if (this.$store.getters['session/hasPaid']) {
        return false;
      }
      return this.shouldShowRateLimit;
    },
  },
  created() {
    this.display();
  },
  methods: {
    display() {
      if (this.$apptimize.enabled()) {
        const widgetDisabled = this.$apptimize.checkExperiments({
          experimentName: 'Today Widget',
          bool: 'todayWidgetDisabled',
          defaultVal: false,
          matcher: /today widget/gi,
        });
        this.show = !widgetDisabled;
      } else {
        this.show = true;
      }
    },
    edit() {
      if (this.shouldShowRateLimit) {
        this.$modal.show('study-goal-set', { goals: this.goals });
        this.$analytics.events.viewedEditStudyGoalFromWidget();
      } else {
        this.$store.commit('ui/setProfileModalShown', 'editProfileModal');
      }
    },
    resetWeek() {
      this.currentWeek = 0;
      this.selectCalendarDate({ date: new Date() });
    },
    selectCalendarDate(date) {
      this.selectedDay = date.date;
      const daysFromToday = differenceInDays(new Date(), this.selectedDay);
      const formattedDateViewed = format(this.selectedDay, 'yyyy-MM-dd');
      this.$analytics.events.viewedDailyProgressTodayWidget(daysFromToday, formattedDateViewed);
    },
    toggleWeek(num) {
      this.currentWeek = this.currentWeek + num;
      this.$analytics.events.adjustedDatePickerTodayWidget();
    },
    todayAction() {
      if (!this.studyGoal) {
        this.$modal.show('study-goal-set', { goals: this.goals });
      } else {
        this.$analytics.events.startedPracticeQuestionsFromToday();
        const navItemId = this.config.config.todayQuickStartNavItemId;
        const navigationItem = this.$store.getters['navigationItems/navigationItems'][navItemId];
        this.$router.push(navigationItem?.route);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin today-breakpoint() {
  @media (max-width: 800px) { @content ;}
}

.today-widget-component-container {
  @include padded-widget;

  .heading {
    align-items: baseline;
    display: flex;
    margin-bottom: rem-calc(24);

    :deep(.dashboard-widget-header) {
      margin-bottom: 0;
    }

    button {
      background: none;
      p {
        color: #767676;
        letter-spacing: 0.5px;
      }
    }
  }

  .widget-container {
    display: flex;

    @include breakpoint(medium) {
      align-items: center;
      flex-direction: column-reverse;
    }

    .left, .right {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(34, 34, 34, 0.16);
      box-sizing: border-box;
      flex: 1;
      height: rem-calc(112);
      position: relative;
      width: 100%;
    }

    .left {
      display: flex;
      margin-right: rem-calc(12);
      padding: rem-calc(16);

      @include breakpoint(medium) {
        margin: 0;
        max-width: rem-calc(480);
      }
    }

    .right {
      margin-left: rem-calc(12);

      @include breakpoint(medium) {
        margin: 0 0 rem-calc(46) 0;
        max-width: rem-calc(480);
      }
      .streak-notch {
        display: flex;
        align-items: flex-end;
        height: rem-calc(56);
        position: absolute;
        bottom: rem-calc(-30);
        left: 50%;
        transform: translateX(-50%);
        padding: rem-calc(7) rem-calc(25);
        border-radius: rem-calc(8);
        background-color: rgba(255, 233, 128, .5);
        font-size: rem-calc(12);
        font-weight: $semi-bold;
        white-space: nowrap;
        z-index: -1;
      }
    }

    .status {
      border: 1px solid black;
      border-radius: 100%;
      box-sizing: border-box;
      height: rem-calc(20);
      position: absolute;
      right: 8px;
      top: 8px;
      width: rem-calc(20);

      &.completed {
        background-color: #00b46d;
        border-color: #00b46d;

        .completed-icon {
          width: rem-calc(20);
        }
      }
    }

    .counter-container {
      background: #001c74;
      border-radius: 6px;
      box-sizing: border-box;
      height: rem-calc(80);
      margin-right: rem-calc(15);
      padding: rem-calc(14);
      width: rem-calc(80);

      .circle-progress-component-container :deep(circle:first-child) {
        stroke: #6276B4;
      }

      .circle-progress-component-container :deep(circle:last-child) {
        stroke: #00d8f8;
      }

      .today-count {
        color: white;
        font-weight: $bold;
      }
    }

    .empty-state {
      margin: rem-calc(5) 0 0 rem-calc(6);
      border: 3px solid white;
      border-radius: 100%;
      width: rem-calc(35);
      height: rem-calc(35);
    }

    .today-text-button {
      flex: 1;
      margin-top: rem-calc(22);
      align-self: flex-start;
      background: transparent;
      padding: rem-calc(4) rem-calc(4) rem-calc(4) 0;
      text-align: left;

      .heading {
        color: #767676;
        font-size: rem-calc(12);
        margin-bottom: rem-calc(4);
      }

      .subheading {
        font-weight: $bold;
      }
    }

    .calendar-heading {
      align-items: center;
      background: #e8e8e8;
      box-sizing: border-box;
      display: flex;
      height: rem-calc(28);
      padding: rem-calc(6) rem-calc(4) rem-calc(5) rem-calc(15);

      .month {
        flex: 1;
        font-size: rem-calc(12);
        margin-right: auto;
      }

      button {
        border-radius: 100%; // needed to crop space around circle icons
        box-sizing: border-box;
        display: flex;
        height: rem-calc(16);
        margin: 0 rem-calc(3);
        padding: 0;
        width: rem-calc(16);

        img {
          display: block;
          height: 100%;
          padding: 0;
          width: 100%;

          &.right-arrow {
            transform: rotate(180deg);
          }
        }

        &.reset {
          background: transparent;
          border: 0;
          padding: 0;

          &:not(.show) img {
            display: none;
          }
        }
      }
    }

    .calendar {
      box-sizing: border-box;
      display: flex;
      height: rem-calc(84);
      justify-content: space-between;
      padding: rem-calc(18) rem-calc(20) 0 rem-calc(20);

      button {
        align-items: center;
        display: flex;
        flex-direction: column;
        padding: 0;
        min-width: rem-calc(30);
        background: transparent;

        &.disabled {
          pointer-events: none;
        }

        .letter {
          font-size: rem-calc(12);
          font-weight: $bold;
          margin-bottom: rem-calc(8);
        }

        .day {
          display: flex;
          align-items: center;
          justify-content: center;
          height: rem-calc(30);
          width: 100%;

          &.completed {
            background-color: #00b46d;
            border-radius: 50%;
            color: white;
            font-weight: $semi-bold;
          }

          &.current {
            font-weight: $bold;
          }

          &.future {
            color: #989898;
            font-weight: $normal;
          }
        }

        .indicator {
          border-top-left-radius: 3px;
          border-top-right-radius: 3px;
          height: 4px;
          margin: auto -6px 0 -6px;
          width: calc(100% + 12px);

          &.active {
            background: black;
          }
        }
      }
    }
    :deep(.rate-limit-widget-component) {
      flex: 1;
      margin: 0;

      @include breakpoint(medium) {
        margin: 0;
        min-height: rem-calc(112) !important;
        max-width: rem-calc(480);
      }
    }
  }
}
</style>
